import { zodResolver } from "@hookform/resolvers/zod"
import { Button } from "design-system/atoms/Button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "design-system/atoms/Dialog"
import { Input } from "design-system/atoms/Input"
import { Tooltip, TooltipContent, TooltipTrigger } from "design-system/atoms/Tooltip"
import { Card, CardDescription, CardHeader, CardTitle } from "design-system/molecules/Card"
import { MarkdownRenderer } from "design-system/organisms/chat/MarkdownRenderer"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "design-system/organisms/Form"
import { cn } from "design-system/utils"
import { getErrorTranslations } from "lib/i18n"
import { PackageSearch } from "lucide-react"
import { useShopifySearchOrderMutation } from "modules/settings/hooks/shopify/useShopifySearchOrderMutation"
import { Loading } from "modules/shared/components/Loading"
import { useState } from "react"
import { useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { GetShopifyOrderInfoSchema, ShopifyGetOrderInfoResponseData } from "schema"
import z from "zod"

type OrderResponseType = z.infer<typeof ShopifyGetOrderInfoResponseData>

type FindOrderProps = {
  isDisabled?: boolean
  disabledReason?: string
}

export const FindOrder = ({ isDisabled, disabledReason }: FindOrderProps) => {
  const { t } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)
  const [order, setOrder] = useState<OrderResponseType | null>(null)
  const form = useForm<z.infer<typeof GetShopifyOrderInfoSchema>>({
    resolver: zodResolver(GetShopifyOrderInfoSchema),
  })
  const { mutateAsync: mutateSearchOrder, isPending: isSearchOrderPending } =
    useShopifySearchOrderMutation()

  const onSubmit = async (data: z.infer<typeof GetShopifyOrderInfoSchema>) => {
    mutateSearchOrder(data)
      .then(setOrder)
      .catch(() => setOrder(null))
  }

  const onOpenChange = (open: boolean) => {
    if (isDisabled && open) return

    setIsOpen(open)
  }

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <Tooltip>
        <DialogTrigger className="w-full" asChild>
          <TooltipTrigger asChild>
            <Card
              className={cn(
                "duration-150 break-all",
                isDisabled ? "cursor-not-allowed opacity-50" : "cursor-pointer hover:bg-muted",
              )}
              aria-disabled={isDisabled}
            >
              <CardHeader className="flex flex-row gap-4">
                <div>
                  <PackageSearch size={32} />
                </div>
                <div className="flex flex-col gap-2 items-start text-left grow wrap-break-word">
                  <CardTitle>{t("settings.integrations.shopify.findOrder.title")}</CardTitle>
                  <CardDescription>
                    {t("settings.integrations.shopify.findOrder.description")}
                  </CardDescription>
                </div>
              </CardHeader>
            </Card>
          </TooltipTrigger>
        </DialogTrigger>
        {isDisabled && disabledReason && <TooltipContent>{disabledReason}</TooltipContent>}
      </Tooltip>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("settings.integrations.shopify.findOrder.title")}</DialogTitle>
          <DialogDescription>
            {t("settings.integrations.shopify.findOrder.description")}
          </DialogDescription>
        </DialogHeader>
        <div className="mt-2">
          <Form
            {...form}
            onSubmit={form.handleSubmit(onSubmit)}
            className="flex flex-col gap-4"
            errorTranslations={getErrorTranslations()}
          >
            <div className="flex flex-col w-full gap-4">
              <FormField
                control={form.control}
                name="orderId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t("settings.integrations.shopify.findOrder.form.shopifyOrderId")}
                    </FormLabel>
                    <FormControl>
                      <Input {...field} required />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div>
              <Button disabled={form.formState.isLoading || !form.formState.isValid}>
                {t("settings.integrations.shopify.findOrder.form.search")}
              </Button>
            </div>
            <div>
              {isSearchOrderPending && <Loading />}
              {!isSearchOrderPending && order && (
                <div className="mt-2">
                  <MarkdownRenderer className="max-h-75 overflow-auto" codeClassName="border-none">
                    {`\`\`\`json\n${JSON.stringify(order, null, 2)}\n\`\`\``}
                  </MarkdownRenderer>
                </div>
              )}
            </div>
          </Form>
        </div>
      </DialogContent>
    </Dialog>
  )
}
